import { CheckCircle } from 'components/icons';
import Loader from 'components/Loader';
import { useModalBack } from 'components/modals/ModalProvider';
import { Button } from 'components/ui/button';
import { CopiedStatus, CopyGlyph } from 'components/util/CopiedStatus';
import useAuth from 'hooks/useAuth';
import { useIsMobile } from 'hooks/useIsMobile';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { AnimatePresence, m } from 'motion/react';
import QRCodeStyling from 'qr-code-styling';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatTimeOfDay } from 'utils/date';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { stepMotion } from 'utils/motion';
import { MFA_CODE_LENGTH, MfaCodeInput } from './MfaCodeInput';
import { buildOtpAuthUrl, classifyMfaCodeError, describeMfaCodeError, formatSetupKey } from './mfaErrors';

type Step = 'scan' | 'manual-key' | 'enrolled';

interface Props {
    /** The account the authenticator entry is created for. */
    email: string;
    /** Named in the intro when the tenant is the one demanding the enrolment. */
    tenantName?: string;
    /** Cross-fades the steps, which takes a `LazyMotion` around it. */
    animated?: boolean;
    /** Runs once Cognito has accepted the code, before the success screen shows. */
    onEnrolled: () => Promise<void>;
    /** The success screen's button. */
    continueLabel: string;
    onContinue: () => void;
    /** The link under the code entry; `undefined` hides it. */
    cancelLabel?: string;
    onCancel?: () => void;
}

const QR_SIZE = 176;

/** The QR code is drawn on a canvas, so the token has to be read out as a concrete colour. */
function primaryColour(): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
    return value || 'black';
}

/**
 * Enrols an authenticator app against the Cognito session held in `cognitoUser`: shows the QR code (or
 * the setup key to type by hand), verifies the first code, then confirms. Used by the sign-in-time
 * enrolment page and by the in-portal security panel.
 */
export function MfaEnrolment({
    email,
    tenantName,
    animated = false,
    onEnrolled,
    continueLabel,
    onContinue,
    cancelLabel,
    onCancel,
}: Props) {
    const { setupTOTP, verifyTOTP } = useAuth();
    const [step, setStep] = useState<Step>('scan');
    const [secret, setSecret] = useState<string | null>(null);
    const [setupFailed, setSetupFailed] = useState(false);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    // Cognito accepted a code: the authenticator is enrolled, whatever the follow-up call does. The
    // cancel link disappears at that point so the only way forward is the follow-up call.
    const [verified, setVerified] = useState(false);
    const [enrolledAt, setEnrolledAt] = useState<Date | null>(null);
    const [copied, setCopied] = useState<'key' | 'link' | null>(null);
    // One timer for both copy buttons: a new copy restarts it, so an earlier copy's reset cannot cut
    // the later one's check short.
    const copiedResetRef = useRef<number | undefined>(undefined);
    const qrRef = useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();
    // Below `sm` the keyboard would cover the QR code the step asks to scan.
    const isMobile = useIsMobile();
    const cancelShown = Boolean(cancelLabel && onCancel && !verified);
    // In a modal, Escape takes the step's way back: the QR code again from the setup key, Cancel from the QR code.
    const backRef = useModalBack<HTMLDivElement>(
        step === 'manual-key' ? () => setStep('scan') : step === 'scan' && cancelShown ? onCancel : undefined
    );

    const qrCode = useMemo(
        () =>
            new QRCodeStyling({
                width: QR_SIZE,
                height: QR_SIZE,
                image: '/favicon.svg',
                dotsOptions: { type: 'rounded', color: primaryColour() },
                backgroundOptions: { color: 'transparent' },
                imageOptions: { crossOrigin: 'anonymous', margin: 4 },
            }),
        []
    );

    useEffect(() => {
        let cancelled = false;
        setupTOTP()
            .then((secretCode) => {
                if (!cancelled) {
                    setSecret(secretCode);
                }
            })
            .catch((err: unknown) => {
                console.error('Error setting up TOTP:', err);
                logError(err);
                if (!cancelled) {
                    setSetupFailed(true);
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one association per mount; the secret must not change under the user
    }, []);

    const otpAuthUrl = secret ? buildOtpAuthUrl(email, secret) : null;

    useEffect(() => {
        if (step === 'scan' && otpAuthUrl && qrRef.current) {
            qrCode.update({ data: otpAuthUrl });
            qrCode.append(qrRef.current);
        }
    }, [qrCode, otpAuthUrl, step]);

    // Takes the code as an argument: the completion callback fires in the same tick as the last digit's
    // change, before the state above has caught up.
    const submitCode = async (candidate: string) => {
        if (submitting) {
            return;
        }
        if (!verified && candidate.length !== MFA_CODE_LENGTH) {
            setCodeError(describeMfaCodeError('incomplete-code'));
            return;
        }
        setSubmitting(true);
        if (!verified) {
            try {
                await verifyTOTP(candidate);
                setVerified(true);
            } catch (err: unknown) {
                const kind = classifyMfaCodeError(err);
                if (kind === 'unknown') {
                    logError(err);
                }
                console.error('Error verifying TOTP:', err);
                setCodeError(describeMfaCodeError(kind));
                setCode('');
                setAttempt((value) => value + 1);
                setSubmitting(false);
                return;
            }
        }
        // Past this point the authenticator is enrolled with Cognito; a failure below is the portal's
        // follow-up call, so Continue retries that call instead of asking for another code.
        try {
            await onEnrolled();
        } catch (err: unknown) {
            logError(err);
            console.error('Error completing the enrolment:', err);
            setCodeError(t('auth.mfa-enrolment-not-recorded'));
            setSubmitting(false);
            return;
        }
        setSubmitting(false);
        setEnrolledAt(new Date());
        setStep('enrolled');
    };

    const copyToClipboard = async (target: 'key' | 'link', value: string | null) => {
        if (!value) {
            return;
        }
        try {
            await navigator.clipboard.writeText(value);
            setCopied(target);
            window.clearTimeout(copiedResetRef.current);
            copiedResetRef.current = window.setTimeout(() => setCopied(null), 1500);
        } catch (err) {
            console.error(`Could not copy the setup ${target}:`, err);
        }
    };

    // Every step is drawn in the one element the way back goes on; animated, the steps cross-fade in it.
    const frame = (content: JSX.Element) => (
        <div ref={backRef} className="relative">
            {animated ? (
                <AnimatePresence initial={false} mode="popLayout">
                    <m.div key={step} {...stepMotion(reduced)}>
                        {content}
                    </m.div>
                </AnimatePresence>
            ) : (
                content
            )}
        </div>
    );

    if (step === 'enrolled') {
        return frame(
            <div className="flex flex-col">
                <div className="bg-success-light mb-5 flex h-14 w-14 items-center justify-center rounded-full">
                    <CheckCircle className="text-success text-[30px]" />
                </div>
                <h1 className="text-2xl font-semibold">{t('auth.mfa-enrolled-title')}</h1>
                <p className="text-muted-foreground mt-2 text-sm">{t('auth.mfa-enrolled-description')}</p>
                <dl className="border-border bg-background my-6 flex flex-col gap-3 rounded-xl border p-4 text-sm">
                    <SummaryRow label={t('auth.mfa-account')} value={email} />
                    <SummaryRow label={t('auth.mfa-method')} value={t('auth.mfa-authenticator-app')} />
                    <SummaryRow
                        label={t('auth.mfa-enabled')}
                        value={t('auth.mfa-enabled-today-at', { time: formatTimeOfDay(enrolledAt ?? new Date()) })}
                    />
                </dl>
                <Button size="lg" className="w-full" autoFocus onClick={onContinue}>
                    {continueLabel}
                </Button>
                <p className="text-muted-foreground mt-4 text-center text-xs">{t('auth.mfa-lost-phone-hint')}</p>
            </div>
        );
    }

    if (step === 'manual-key') {
        return frame(
            <div className="flex flex-col">
                <h1 className="text-2xl font-semibold">{t('auth.mfa-manual-title')}</h1>
                <p className="text-muted-foreground mt-2 mb-6 text-sm">{t('auth.mfa-manual-description')}</p>
                <p className="text-muted-foreground mb-1.5 text-sm">{t('auth.mfa-setup-key')}</p>
                <div className="border-input bg-background mb-4 flex items-center gap-3 rounded-[10px] border py-3 pr-3 pl-4">
                    <code className="flex-1 font-mono text-[15px] tracking-[1.5px] break-all">
                        {secret ? formatSetupKey(secret) : ''}
                    </code>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            void copyToClipboard('key', secret);
                        }}
                    >
                        <CopyGlyph copied={copied === 'key'} />
                        {t('copy')}
                    </Button>
                    <CopiedStatus copied={copied === 'key'} />
                </div>
                <dl className="border-border bg-background flex flex-col gap-3 rounded-xl border p-4 text-sm">
                    <SummaryRow label={t('auth.mfa-account-name')} value={`Crespect: ${email}`} />
                    <SummaryRow label={t('auth.mfa-key-type')} value={t('auth.mfa-key-type-time-based')} />
                    <SummaryRow label={t('auth.mfa-key-digits')} value={String(MFA_CODE_LENGTH)} />
                </dl>
                <p className="text-muted-foreground mt-6 mb-1.5 text-sm">{t('auth.mfa-setup-link')}</p>
                <div className="border-input bg-background flex items-center gap-3 rounded-[10px] border py-3 pr-3 pl-4">
                    <code className="flex-1 font-mono text-xs break-all">{otpAuthUrl ?? ''}</code>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            void copyToClipboard('link', otpAuthUrl);
                        }}
                    >
                        <CopyGlyph copied={copied === 'link'} />
                        {t('copy')}
                    </Button>
                    <CopiedStatus copied={copied === 'link'} />
                </div>
                <p className="text-muted-foreground mt-1.5 text-xs">{t('auth.mfa-setup-link-description')}</p>
                <div className="mt-5 text-center">
                    <Button variant="link" onClick={() => setStep('scan')}>
                        {t('auth.mfa-back-to-qr')}
                    </Button>
                </div>
            </div>
        );
    }

    return frame(
        <div className="flex flex-col">
            <h1 className="text-2xl font-semibold">{t('auth.mfa-setup-title')}</h1>
            <p className="text-muted-foreground mt-2 mb-6 text-sm">
                {tenantName ? t('auth.mfa-setup-description-tenant', { tenantName }) : t('auth.mfa-setup-description')}
            </p>

            <h2 className="text-base font-semibold">{t('auth.mfa-step-scan')}</h2>
            <p className="text-muted-foreground mb-3 text-xs">{t('auth.mfa-step-scan-apps')}</p>
            <div className="border-border bg-background flex flex-col items-center gap-3 rounded-xl border p-5">
                {setupFailed ? (
                    <p className="text-destructive text-sm" role="alert">
                        {t('auth.qr-code-mfa-could-not-setup')}
                    </p>
                ) : secret ? (
                    <div ref={qrRef} className="h-[176px] w-[176px]" />
                ) : (
                    <div className="h-[176px] w-[176px]">
                        <Loader />
                    </div>
                )}
                {secret ? (
                    <Button variant="link" onClick={() => setStep('manual-key')}>
                        {t('auth.mfa-cannot-scan')}
                    </Button>
                ) : null}
            </div>

            <h2 className="mt-6 mb-3 text-base font-semibold">{t('auth.mfa-step-enter-code')}</h2>
            <MfaCodeInput
                // Remounted as the secret arrives, so the field, enabled, takes the focus.
                key={secret ? 'ready' : 'waiting'}
                value={code}
                onChange={(value) => {
                    setCode(value);
                    setCodeError(null);
                }}
                onComplete={(value) => {
                    void submitCode(value);
                }}
                error={Boolean(codeError)}
                disabled={!secret || submitting || verified}
                autoFocus={attempt > 0 || !isMobile}
                resetKey={attempt}
                ariaLabel={t('auth.mfa-code-label')}
            />
            <p
                className={codeError ? 'text-destructive mt-2.5 text-xs' : 'text-muted-foreground mt-2.5 text-xs'}
                role={codeError ? 'alert' : undefined}
            >
                {codeError ?? t('auth.mfa-code-rotates')}
            </p>

            <Button
                size="lg"
                className="mt-6 w-full"
                disabled={!secret || submitting || (!verified && code === '')}
                onClick={() => {
                    void submitCode(code);
                }}
            >
                {t('continue')}
            </Button>
            {cancelShown ? (
                <div className="mt-4 text-center">
                    <Button variant="link" onClick={onCancel}>
                        {cancelLabel}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="m-0 text-right font-semibold break-all">{value}</dd>
        </div>
    );
}
